import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SignalForm } from "@/components/admin/SignalForm";
import { fetchLiveSignals } from "@/lib/signals-queries";
import { updateLiveSignal, type SignalFormValues } from "@/lib/admin-mutations";

export const Route = createFileRoute("/_authenticated/admin/signals/$id/edit")({
  component: EditSignalPage,
});

function EditSignalPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: signals, isLoading } = useQuery({
    queryKey: ["admin", "live"],
    queryFn: fetchLiveSignals,
  });
  const signal = signals?.find((s) => s.id === id);

  const m = useMutation({
    mutationFn: (values: SignalFormValues) => updateLiveSignal(id, values),
    onSuccess: () => {
      toast.success("Signal updated");
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["live-signals"] });
      navigate({ to: "/admin/signals" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  }
  if (!signal) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-8 text-center">
        <p className="text-muted-foreground">Signal not found.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/admin/signals">Back to signals</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/signals"><ArrowLeft className="h-4 w-4" /> Back to signals</Link>
        </Button>
        <h1 className="mt-2 font-display text-3xl font-bold">Edit signal</h1>
        <p className="mt-1 text-sm text-muted-foreground">Editing live (members) signal <span className="font-mono">{id}</span>.</p>
      </div>
      <SignalForm
        editing
        initial={{
          id: signal.id,
          asset: signal.asset,
          type: signal.type,
          order_type: signal.orderType,
          timeframe: signal.timeframe,
          style: "Intraday",
          entry: signal.entry ?? 0,
          sl: signal.sl ?? 0,
          tp1: signal.tp1 ?? 0,
          tp2: signal.tp2 ?? 0,
          note: signal.note,
          posted_label: signal.postedAt,
          position: 0,
          publish_live: true,
          publish_public: false,
          // ORION-LIVE-L1 · R2-3 — the deadline is hydrated from the stored
          // row and re-submitted with the order type in one operation, so an
          // unrelated edit can neither drop it nor leave a resting order
          // without one. The control expects a UTC `datetime-local` value.
          pending_expires_at: signal.pendingExpiresAt
            ? signal.pendingExpiresAt.slice(0, 16)
            : null,
        }}
        onSubmit={(values) => m.mutate(values)}
        submitting={m.isPending}
        submitLabel="Save changes"
      />
    </div>
  );
}
